/* eslint-disable jsx-a11y/click-events-have-key-events */
/* eslint-disable jsx-a11y/no-noninteractive-element-interactions */
import React, { useLayoutEffect, useState } from 'react';
import { Button, Layout, Menu, message, Skeleton, Tooltip } from 'antd';
import { useNavigate } from 'react-router-dom';

import {
  AppstoreOutlined,
  BankOutlined,
  BookOutlined,
  CalendarOutlined,
  DollarOutlined,
  InboxOutlined,
  MenuFoldOutlined,
  MenuUnfoldOutlined,
} from '@ant-design/icons';

import logo from '../../Assets/Images/logo-inline.png';
import Utils from '../../Assets/Scripts/Utils';
import { api } from '../../Services/axiosService';

import './SidebarMenu.scss';

const { Sider } = Layout;
const { SubMenu } = Menu;

export default function SidebarMenu() {
  const [permissions, setPermissions] = useState({});
  const [companyId, setCompanyId] = useState(null);
  const [collapsed, setCollapsed] = useState(
    JSON.parse(localStorage.getItem('conecta__menuPreference'))
  );
  const [loading, setLoading] = useState(true);
  const navigate = useNavigate();

  const toggleCollapsed = () => {
    localStorage.setItem('conecta__menuPreference', JSON.stringify(!collapsed));
    setCollapsed(!collapsed);
  };

  const getResources = () => {
    setLoading(true);
    api
      .get('/Resource')
      .then((res) => {
        const userPermissions = {};

        // Get user permissions from localStorage
        const storedPermissions = JSON.parse(
          localStorage.getItem('conecta__permissions')
        )?.resources;

        if (storedPermissions) {
          storedPermissions.forEach(({ name, canView, canHandle, selfView }) => {
            userPermissions[name] = { canView, canHandle, selfView };
          });
        }

        const storedRole = JSON.parse(localStorage.getItem('conecta__permissions'));
        const agendaCtPrivilegedRoleIds = [21, 1];
        if (storedRole?.id != null && agendaCtPrivilegedRoleIds.includes(storedRole.id)) {
          userPermissions.ScheduleTrainingCenter = {
            canView: true,
            canHandle: userPermissions.ScheduleTrainingCenter?.canHandle ?? true,
            selfView: userPermissions.ScheduleTrainingCenter?.selfView ?? false,
          };
        }

        // Update permissions with showMenu property
        if (res.data) {
          res.data.forEach(({ model, showMenu }) => {
            if (userPermissions[model]) {
              userPermissions[model].ShowMenu = showMenu;
            }
          });
        }

        setPermissions(userPermissions);
        setLoading(false);
      })
      .catch((error) => {
        Utils.logError(error);
        message.error('Oops. Algo deu errado ao buscar os recursos do menu!');
        setLoading(false);
      });
  };

  // Handle menu item click - only navigate if it's not a middle click or modified click (e.g., Ctrl+click)
  const handleMenuItemClick = (e, path) => {
    // Don't navigate via the React Router if the link will be handled by the browser
    if (e.button === 0 && !e.ctrlKey && !e.metaKey && !e.shiftKey) {
      e.preventDefault();
      navigate(path);
    }
  };

  useLayoutEffect(() => {
    // Get user company ID from localStorage
    const userCompanyId = JSON.parse(localStorage.getItem('conecta__userData'))?.companyId;
    setCompanyId(userCompanyId);

    // Fetch resources/permissions data
    getResources();
  }, []);

  // Create a menu item with an anchor tag for proper context menu support
  const renderMenuItem = (key, title) => (
    <Menu.Item key={key} title={title}>
      <Tooltip title={title} placement="right" mouseEnterDelay={0.5}>
        <a
          href={key}
          onClick={(e) => handleMenuItemClick(e, key)}
          style={{
            textDecoration: 'none',
            color: 'inherit',
            display: 'block',
            width: '100%',
            height: '100%',
            overflow: 'hidden',
            textOverflow: 'ellipsis',
            whiteSpace: 'nowrap',
          }}
        >
          {title}
        </a>
      </Tooltip>
    </Menu.Item>
  );

  // Create a submenu with tooltip
  const renderSubMenu = (key, icon, title, children) => (
    <SubMenu
      key={key}
      icon={icon}
      title={
        <Tooltip title={title} placement="right" mouseEnterDelay={0.5}>
          <span>{title}</span>
        </Tooltip>
      }
    >
      {children}
    </SubMenu>
  );

  // Check if any of the Agendas items should be shown
  const showAgendasGroup = () =>
    (permissions.ScheduleOverview?.canView && permissions.ScheduleOverview?.ShowMenu) ||
    (permissions.ScheduleTrainingCenter?.canView && permissions.ScheduleTrainingCenter?.ShowMenu) ||
    (permissions.ScheduleInstructor?.canView && permissions.ScheduleInstructor?.ShowMenu) ||
    (permissions.ScheduleDriver?.canView && permissions.ScheduleDriver?.ShowMenu) ||
    (permissions.InstructorManagement?.canView && permissions.InstructorManagement?.ShowMenu);

  // Check if any of the Cadastros Principais items should be shown
  const showCadastrosPrincipaisGroup = () =>
    (permissions.TrainingCenter?.canView && permissions.TrainingCenter?.ShowMenu) ||
    (permissions.TrainingCenter?.selfView && permissions.TrainingCenter?.ShowMenu) ||
    (permissions.Company?.canView && permissions.Company?.ShowMenu) ||
    (permissions.User?.canView && permissions.User?.ShowMenu) ||
    (permissions.Role?.canView && permissions.Role?.ShowMenu) ||
    (permissions.Product?.canView && permissions.Product?.ShowMenu);

  // Check if any of the Treinamentos items should be shown
  const showTreinamentosGroup = () =>
    (permissions.TrainingList?.canView && permissions.TrainingList?.ShowMenu) ||
    (permissions.Certificates?.canView && permissions.Certificates?.ShowMenu);

  // Check if any of the Financeiro items should be shown
  const showFinanceiroGroup = () => permissions.Billing?.canView && permissions.Billing?.ShowMenu;

  // Check if any of the Logística items should be shown
  const showLogisticaGroup = () =>
    (permissions.Instructor?.canView && permissions.Instructor?.ShowMenu) ||
    (permissions.Instructor?.selfView && permissions.Instructor?.ShowMenu) ||
    (permissions.PreInstructor?.canView && permissions.PreInstructor?.ShowMenu) ||
    (permissions.Driver?.canView && permissions.Driver?.ShowMenu) ||
    (permissions.Driver?.selfView && permissions.Driver?.ShowMenu) ||
    (permissions.Transport?.canView && permissions.Transport?.ShowMenu) ||
    (permissions.Transport?.selfView && permissions.Transport?.ShowMenu) ||
    (permissions.Vehicle?.canView && permissions.Vehicle?.ShowMenu);

  // Check if any of the Cadastros Auxiliares items should be shown
  const showCadastrosAuxiliaresGroup = () =>
    (permissions.ReasonNoBilling?.canView && permissions.ReasonNoBilling?.ShowMenu) ||
    (permissions.VehicleType?.canView && permissions.VehicleType?.ShowMenu) ||
    (permissions.DocumentType?.canView && permissions.DocumentType?.ShowMenu) ||
    (permissions.OccurrenceType?.canView && permissions.OccurrenceType?.ShowMenu) ||
    (permissions.Occurrence?.canView && permissions.Occurrence?.ShowMenu) ||
    (permissions.DenyTaskReason?.canView && permissions.DenyTaskReason?.ShowMenu) ||
    (permissions.InvoiceIssuer?.canView && permissions.InvoiceIssuer?.ShowMenu);

  return (
    <div className="siderBar-menu">
      <Button type="primary" onClick={toggleCollapsed}>
        {collapsed ? <MenuUnfoldOutlined /> : <MenuFoldOutlined />}
      </Button>
      <img
        src={logo}
        title="Conecta Treinamentos"
        alt="Logo Conecta Treinamentos"
        onClick={() => navigate('/')}
      />
      <div className="background-menu">
        <Sider
          theme="light"
          collapsed={collapsed}
          onCollapse={(value) => setCollapsed(value)}
          width={200}
          collapsedWidth={80}
          style={{ height: '100%' }}
        >
          <Skeleton
            active
            loading={loading}
            paragraph={{ rows: 10 }}
            title={false}
            className="menu-skeleton"
          >
            {!loading && Object.keys(permissions).length > 0 && (
              <Menu
                theme="light"
                mode="inline"
                defaultSelectedKeys={[window.location.pathname]}
                defaultOpenKeys={
                  collapsed
                    ? []
                    : [
                        'agendas',
                        'cadastrosPrincipais',
                        'treinamentos',
                        'financeiro',
                        'logistica',
                        'cadastrosAuxiliares',
                      ]
                }
                style={{ height: '100%', borderRight: 0 }}
              >
                {/* 1. Agendas Group */}
                {showAgendasGroup() &&
                  renderSubMenu(
                    'agendas',
                    <CalendarOutlined />,
                    'Agendas',
                    <>
                      {/* Items sorted alphabetically */}
                      {permissions.ScheduleOverview?.canView &&
                        permissions.ScheduleOverview?.ShowMenu &&
                        renderMenuItem('/Agenda/VisaoGeral', 'Agenda')}

                      {permissions.ScheduleTrainingCenter?.canView &&
                        permissions.ScheduleTrainingCenter?.ShowMenu &&
                        renderMenuItem('/Agenda/CentroTreinamento', 'Agenda CT')}

                      {permissions.ScheduleInstructor?.canView &&
                        permissions.ScheduleInstructor?.ShowMenu &&
                        renderMenuItem('/Agenda/Instrutor', 'Agenda Instrutor')}

                      {permissions.ScheduleDriver?.canView &&
                        permissions.ScheduleDriver?.ShowMenu &&
                        renderMenuItem('/Agenda/Motorista', 'Agenda Motorista')}

                      {permissions.InstructorManagement?.canView &&
                        permissions.InstructorManagement?.ShowMenu &&
                        renderMenuItem('/GestaoInstrutores', 'Gestão Instrutores')}
                    </>
                  )}

                {/* 2. Cadastros Principais Group */}
                {showCadastrosPrincipaisGroup() &&
                  renderSubMenu(
                    'cadastrosPrincipais',
                    <BankOutlined />,
                    'Cadastros Principais',
                    <>
                      {/* Items sorted alphabetically */}
                      {permissions.Role?.canView &&
                        permissions.Role?.ShowMenu &&
                        renderMenuItem('/Cargo/Listar', 'Cargo')}

                      {permissions.TrainingCenter?.canView &&
                        permissions.TrainingCenter?.ShowMenu &&
                        renderMenuItem('/CentroTreinamento/Listar', 'Centro De Treinamento')}

                      {permissions.TrainingCenter?.selfView &&
                        permissions.TrainingCenter?.ShowMenu &&
                        renderMenuItem(
                          `/CentroTreinamento/Editar/${companyId}`,
                          'Centro De Treinamento'
                        )}

                      {permissions.Company?.canView &&
                        permissions.Company?.ShowMenu &&
                        renderMenuItem('/Empresas/Listar', 'Empresas')}

                      {permissions.Product?.canView &&
                        permissions.Product?.ShowMenu &&
                        renderMenuItem('/Produtos/Listar', 'Produtos')}

                      {permissions.User?.canView &&
                        permissions.User?.ShowMenu &&
                        renderMenuItem('/Usuario/Listar', 'Usuário')}
                    </>
                  )}

                {/* 3. Treinamentos Group */}
                {showTreinamentosGroup() &&
                  renderSubMenu(
                    'treinamentos',
                    <BookOutlined />,
                    'Treinamentos',
                    <>
                      {/* Items sorted alphabetically */}
                      {permissions.Certificates?.canView &&
                        permissions.Certificates?.ShowMenu &&
                        renderMenuItem('/Certificados/Listar', 'Certificados')}

                      {permissions.TrainingList?.canView &&
                        permissions.TrainingList?.ShowMenu &&
                        renderMenuItem('/Treinamentos/Listar', 'Lista de Treinamentos')}
                    </>
                  )}

                {/* 4. Financeiro Group */}
                {showFinanceiroGroup() &&
                  renderSubMenu(
                    'financeiro',
                    <DollarOutlined />,
                    'Financeiro',
                    permissions.Billing?.canView &&
                      permissions.Billing?.ShowMenu &&
                      renderMenuItem('/Faturamento', 'Faturamento')
                  )}

                {/* 5. Logística Group */}
                {showLogisticaGroup() &&
                  renderSubMenu(
                    'logistica',
                    <InboxOutlined />,
                    'Logística',
                    <>
                      {/* Items sorted alphabetically */}
                      {permissions.Instructor?.canView &&
                        permissions.Instructor?.ShowMenu &&
                        renderMenuItem('/Instrutor/Listar', 'Instrutor')}

                      {permissions.Instructor?.selfView &&
                        permissions.Instructor?.ShowMenu &&
                        renderMenuItem(`/Instrutor/Editar/${companyId}`, 'Instrutor')}

                      {permissions.Driver?.canView &&
                        permissions.Driver?.ShowMenu &&
                        renderMenuItem('/Motorista/Listar', 'Motorista')}

                      {permissions.Driver?.selfView &&
                        permissions.Driver?.ShowMenu &&
                        renderMenuItem(`/Motorista/Editar/${companyId}`, 'Motorista')}

                      {permissions.PreInstructor?.canView &&
                        permissions.PreInstructor?.ShowMenu &&
                        renderMenuItem('/PreCadastroInstrutor', 'Pré-Registro de Instrutor')}

                      {permissions.Transport?.canView &&
                        permissions.Transport?.ShowMenu &&
                        renderMenuItem('/Transporte/Listar', 'Transporte')}

                      {permissions.Transport?.selfView &&
                        permissions.Transport?.ShowMenu &&
                        renderMenuItem(`/Transporte/Editar/${companyId}`, 'Transporte')}

                      {permissions.Vehicle?.canView &&
                        permissions.Vehicle?.ShowMenu &&
                        renderMenuItem('/Veiculo/Listar', 'Veículo')}
                    </>
                  )}

                {/* 6. Cadastros Auxiliares Group */}
                {showCadastrosAuxiliaresGroup() &&
                  renderSubMenu(
                    'cadastrosAuxiliares',
                    <AppstoreOutlined />,
                    'Cadastros Auxiliares',
                    <>
                      {/* Items sorted alphabetically */}
                      {permissions.InvoiceIssuer?.canView &&
                        permissions.InvoiceIssuer?.ShowMenu &&
                        renderMenuItem('/EmissorNotaFiscal/Listar', 'Emissor Nota Fiscal')}

                      {permissions.Occurrence?.canView &&
                        permissions.Occurrence?.ShowMenu &&
                        renderMenuItem('/GestaoOcorrencia', 'Gestão de ocorrência')}

                      {permissions.DenyTaskReason?.canView &&
                        permissions.DenyTaskReason?.ShowMenu &&
                        renderMenuItem('/MotivoDescarte/Listar', 'Motivo de Descarte')}

                      {permissions.ReasonNoBilling?.canView &&
                        permissions.ReasonNoBilling?.ShowMenu &&
                        renderMenuItem('/MotivoNaoFaturamento/Listar', 'Motivo de Não Faturamento')}

                      {permissions.Occurrence?.canView &&
                        permissions.Occurrence?.ShowMenu &&
                        renderMenuItem('/Ocorrencia/Listar', 'Ocorrência')}

                      {permissions.DocumentType?.canView &&
                        permissions.DocumentType?.ShowMenu &&
                        renderMenuItem('/TipoDocumento/Listar', 'Tipo de Documento')}

                      {permissions.OccurrenceType?.canView &&
                        permissions.OccurrenceType?.ShowMenu &&
                        renderMenuItem('/TipoOcorrencia/Listar', 'Tipo de Ocorrência')}

                      {permissions.VehicleType?.canView &&
                        permissions.VehicleType?.ShowMenu &&
                        renderMenuItem('/TipoVeiculo/Listar', 'Tipo de Veículo')}
                    </>
                  )}
              </Menu>
            )}
          </Skeleton>
        </Sider>
      </div>
    </div>
  );
}
